import {
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Heading,
  HStack,
  Icon,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useState } from 'react';
import { FiBell, FiCheck, FiCheckCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMyNotifications, markNotificationAsRead, markAllNotificationsAsRead } from '../../hooks/useNotifications';
import { formatRelativeTime } from '../../utils/formatters';
import { LoadingSpinner } from '../common/LoadingSpinner';

export default function NotificationsList() {
  const { currentUser } = useAuth();
  const { notifications, loading } = useMyNotifications(currentUser?.uid);
  const navigate = useNavigate();
  const toast = useToast();
  const [marking, setMarking] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const unreadBg = useColorModeValue('orange.50', 'orange.900');
  const readBg = useColorModeValue('white', 'gray.800');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await markNotificationAsRead(notification.id);
    }
    navigate(notification.actionUrl);
  };

  const handleMarkAllAsRead = async () => {
    if (!currentUser) return;
    setMarking(true);
    try {
      await markAllNotificationsAsRead(currentUser.uid);
      toast({
        title: 'All notifications marked as read',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to mark all as read',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setMarking(false);
    }
  };

  // Group notifications by date
  const groupNotificationsByDate = (notifications) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);

    const groups = {
      Today: [],
      Yesterday: [],
      'This Week': [],
      Older: [],
    };

    notifications.forEach((notification) => {
      const notifDate = notification.createdAt?.toDate
        ? notification.createdAt.toDate()
        : new Date(notification.createdAt);

      if (notifDate >= today) {
        groups.Today.push(notification);
      } else if (notifDate >= yesterday) {
        groups.Yesterday.push(notification);
      } else if (notifDate >= lastWeek) {
        groups['This Week'].push(notification);
      } else {
        groups.Older.push(notification);
      }
    });

    return groups;
  };

  if (loading) return <LoadingSpinner message="Loading notifications..." />;

  const groupedNotifications = groupNotificationsByDate(notifications);
  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <Container maxW="3xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="start">
          <Box>
            <Heading size="lg">Notifications</Heading>
            {unreadCount > 0 && (
              <Text color="gray.600" mt={1} fontSize="sm">
                {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
              </Text>
            )}
          </Box>
          {unreadCount > 0 && (
            <Button
              size="sm"
              variant="outline"
              leftIcon={<Icon as={FiCheckCircle} />}
              onClick={handleMarkAllAsRead}
              isLoading={marking}
            >
              Mark all as read
            </Button>
          )}
        </HStack>

        {/* Notifications */}
        {notifications.length === 0 ? (
          <Box
            bg={cardBg}
            rounded="xl"
            p={12}
            borderWidth={1}
            borderColor={borderColor}
            textAlign="center"
          >
            <VStack spacing={3}>
              <Icon as={FiBell} boxSize={12} color="gray.300" />
              <Heading size="md" color="gray.500">No notifications yet</Heading>
              <Text color="gray.400" fontSize="sm">
                You'll see notifications here when there's activity on your tasks and submissions
              </Text>
            </VStack>
          </Box>
        ) : (
          <VStack spacing={6} align="stretch">
            {Object.entries(groupedNotifications).map(([groupName, groupNotifications]) => {
              if (groupNotifications.length === 0) return null;

              return (
                <Box key={groupName}>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="wide"
                    mb={3}
                  >
                    {groupName}
                  </Text>
                  <VStack spacing={2} align="stretch">
                    {groupNotifications.map((notification) => (
                      <Box
                        key={notification.id}
                        bg={notification.read ? readBg : unreadBg}
                        rounded="lg"
                        p={4}
                        borderWidth={1}
                        borderColor={borderColor}
                        cursor="pointer"
                        _hover={{ bg: hoverBg }}
                        onClick={() => handleNotificationClick(notification)}
                        transition="all 0.2s"
                      >
                        <HStack align="start" spacing={3}>
                          {!notification.read && (
                            <Box
                              w="8px"
                              h="8px"
                              borderRadius="full"
                              bg="orange.500"
                              flexShrink={0}
                              mt="6px"
                            />
                          )}
                          {notification.read && (
                            <Icon as={FiCheck} color="green.500" flexShrink={0} mt="2px" />
                          )}
                          <VStack align="start" spacing={1} flex="1">
                            <HStack justify="space-between" w="full">
                              <Text
                                fontSize="sm"
                                fontWeight={notification.read ? 'normal' : 'semibold'}
                              >
                                {notification.title}
                              </Text>
                              <Text fontSize="xs" color="gray.400" flexShrink={0} ml={2}>
                                {formatRelativeTime(notification.createdAt)}
                              </Text>
                            </HStack>
                            <Text fontSize="sm" color="gray.600">
                              {notification.message}
                            </Text>
                            {notification.bountyTitle && (
                              <Badge colorScheme="orange" fontSize="xs" mt={1}>
                                {notification.bountyTitle}
                              </Badge>
                            )}
                          </VStack>
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                </Box>
              );
            })}
          </VStack>
        )}
      </VStack>
    </Container>
  );
}
